// ReviewItemGroupDrawer — decides a grouped review entry (reviewItemGroups)
// in one action. Every member is still its own review item: the drawer sends
// each one its own decision, the same decision its single drawer would send.
//
//   - teams: each stated team with the row it was read from, its name
//     editable, and "Not a team" to leave that one blank. One button confirms
//     them all.
//   - same: the one conflict the lines share, its choices, and one button
//     that sets the picked value on every open line (or leaves them blank).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraFormField, SpecraInput, SpecraOverlayDrawer, SpecraSectionHeader, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useIsViewOnly } from '../../../../../contexts/authContext';
import { useResolveDiscrepancyGroup } from '../../../PreAward/awardCompareHooks';
import { teamCorrectedValueJson } from '../../lib/correctedValue';
import { displayValue, enteredRationale, firstPage, isOpenReviewItem, leftBlankRationale, pickRationale, recordLabel, resolutionLabel } from './reviewItemCopy';
import { groupTitle, statedTeamName } from './reviewItemGroups';
const NOT_A_TEAM = 'Not a team: the billing table names it, but it is not one of the program’s teams';
export function ReviewItemGroupDrawer({ group, open, onClose, changeSetId }) {
    const onOpenChange = (next)=>{
        if (!next) onClose();
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open && !!group,
        onOpenChange: onOpenChange,
        eyebrow: group ? group.mode === 'teams' ? 'Teams' : `${group.members.length} lines` : undefined,
        title: group ? groupTitle(group) : '',
        testId: Testids.reviewItem.groupDrawer,
        width: "lg",
        children: group && (group.mode === 'teams' ? /*#__PURE__*/ _jsx(TeamGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose
        }, group.id) : /*#__PURE__*/ _jsx(SameValueGroup, {
            group: group,
            changeSetId: changeSetId,
            onDone: onClose
        }, group.id))
    });
}
function source(r) {
    return {
        quote: r.sources[0]?.quote ?? '',
        page: firstPage(r.sources)
    };
}
function TeamGroup({ group, changeSetId, onDone }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const [names, setNames] = useState(()=>Object.fromEntries(group.members.map((m)=>[
                m.id,
                statedTeamName(m.review)
            ])));
    const [rejected, setRejected] = useState({});
    const openMembers = group.members.filter(isOpenReviewItem);
    const decisions = useMemo(()=>{
        return openMembers.map((m)=>{
            if (rejected[m.id]) {
                return {
                    discrepancyId: m.id,
                    resolutionState: 'Waived',
                    decisionRationale: leftBlankRationale(NOT_A_TEAM, m.review)
                };
            }
            const stated = m.review.candidates.find((c)=>!c.workstreamId) ?? m.review.candidates[0];
            const name = (names[m.id] ?? '').trim();
            const existing = m.review.candidates.find((c)=>c.workstreamId && c.label === name);
            const edited = !stated || name !== stated.label;
            return {
                discrepancyId: m.id,
                resolutionState: 'Corrected',
                decisionRationale: edited || !stated ? enteredRationale(name, '') : pickRationale(stated, ''),
                decidedValueJson: existing?.workstreamId ? teamCorrectedValueJson({
                    workstreamId: existing.workstreamId
                }) : teamCorrectedValueJson({
                    newTeamName: name
                })
            };
        });
    }, [
        openMembers,
        names,
        rejected
    ]);
    const confirming = decisions.filter((d)=>d.resolutionState === 'Corrected').length;
    const blankName = decisions.some((d)=>d.resolutionState === 'Corrected' && !d.decidedValueJson);
    const onConfirm = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Confirmed ${confirming} team${confirming === 1 ? '' : 's'}${decisions.length > confirming ? `, ${decisions.length - confirming} left blank` : ''}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "The award names these teams in its billing table. Confirm them together: edit a name if it is printed differently from how you call the team, or mark one that is not a team."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border/60",
                children: group.members.map((m)=>{
                    const open = isOpenReviewItem(m);
                    const { quote, page } = source(m.review);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5 py-2.5",
                        "data-testid": Testids.reviewItem.groupMember,
                        "data-item-id": m.id,
                        "data-resolution": m.resolutionState,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    open ? /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": `Team name (${statedTeamName(m.review)})`,
                                        value: names[m.id] ?? '',
                                        disabled: !!rejected[m.id] || isViewOnly,
                                        onChange: (e)=>setNames((prev)=>({
                                                    ...prev,
                                                    [m.id]: e.target.value
                                                })),
                                        "data-testid": Testids.reviewItem.groupMemberName,
                                        className: "max-w-xs"
                                    }) : /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: statedTeamName(m.review)
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex-1"
                                    }),
                                    open ? /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                        label: "Not a team",
                                        "data-testid": Testids.reviewItem.groupMemberReject,
                                        controlProps: {
                                            checked: !!rejected[m.id],
                                            disabled: isViewOnly,
                                            onCheckedChange: (v)=>setRejected((prev)=>({
                                                        ...prev,
                                                        [m.id]: v === true
                                                    }))
                                        }
                                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "success",
                                        children: resolutionLabel(m.resolutionState, m.review)
                                    })
                                ]
                            }),
                            quote && /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    "“",
                                    quote.length > 220 ? `${quote.slice(0, 219)}…` : quote,
                                    "”",
                                    page > 0 ? ` · p. ${page}` : ''
                                ]
                            })
                        ]
                    }, m.id);
                })
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    disabled: isViewOnly || resolve.isPending || blankName,
                    onClick: ()=>void onConfirm(),
                    "data-testid": Testids.reviewItem.groupConfirm,
                    children: resolve.isPending ? 'Saving…' : `Confirm ${confirming} team${confirming === 1 ? '' : 's'}${decisions.length > confirming ? ` (${decisions.length - confirming} not a team)` : ''}`
                })
            })
        ]
    });
}
function SameValueGroup({ group, changeSetId, onDone }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancyGroup();
    const first = group.members[0].review;
    const field = first.targetFieldPath;
    const [choice, setChoice] = useState('');
    const [blanking, setBlanking] = useState(false);
    const [reason, setReason] = useState('');
    const openMembers = group.members.filter(isOpenReviewItem);
    const { quote, page } = source(first);
    const lines = group.members.map((m)=>recordLabel(m.review.targetObjectType, m.review.targetLocator));
    const onApply = async ()=>{
        const decisions = [];
        for (const m of openMembers){
            const c = m.review.candidates.find((x)=>x.key === choice);
            if (!c) continue;
            decisions.push({
                discrepancyId: m.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(c, '', field),
                decidedValueJson: c.valueJson
            });
        }
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions,
                summary: `Set on ${decisions.length} line${decisions.length === 1 ? '' : 's'}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    const onBlank = async ()=>{
        try {
            await resolve.mutateAsync({
                changeSetId,
                decisions: openMembers.map((m)=>({
                        discrepancyId: m.id,
                        resolutionState: 'Waived',
                        decisionRationale: leftBlankRationale(reason.trim(), m.review)
                    })),
                summary: `Left blank on ${openMembers.length} line${openMembers.length === 1 ? '' : 's'}`
            });
            onDone();
        } catch  {
        /* toasted */ }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: first.reason
            }),
            quote && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    "“",
                    quote.length > 300 ? `${quote.slice(0, 299)}…` : quote,
                    "”",
                    page > 0 ? ` · p. ${page}` : ''
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: `Applies to ${group.members.length} lines`
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        className: "mt-1",
                        children: lines.join(', ')
                    })
                ]
            }),
            openMembers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Your call"
                    }),
                    first.candidates.map((c)=>/*#__PURE__*/ _jsx(SpecraButton, {
                            variant: choice === c.key ? 'default' : 'secondary',
                            className: "w-full justify-start",
                            disabled: isViewOnly,
                            onClick: ()=>setChoice(c.key),
                            "data-testid": Testids.reviewItem.groupChoice,
                            "data-key": c.key,
                            "aria-pressed": choice === c.key,
                            children: displayValue(c.valueJson, field) !== c.label && c.label ? c.label : displayValue(c.valueJson, field)
                        }, c.key)),
                    blanking && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Why leave these blank?",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: reason,
                            onChange: (e)=>setReason(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            blanking ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "destructive",
                                size: "sm",
                                disabled: !reason.trim() || isViewOnly || resolve.isPending,
                                onClick: ()=>void onBlank(),
                                "data-testid": Testids.reviewItem.groupLeaveBlank,
                                children: [
                                    "Leave ",
                                    openMembers.length,
                                    " blank"
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "destructive",
                                size: "sm",
                                disabled: isViewOnly,
                                onClick: ()=>setBlanking(true),
                                "data-testid": Testids.reviewItem.groupLeaveBlank,
                                children: "Leave them blank…"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex-1"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !choice || isViewOnly || resolve.isPending,
                                onClick: ()=>void onApply(),
                                "data-testid": Testids.reviewItem.groupConfirm,
                                children: resolve.isPending ? 'Saving…' : `Set on ${openMembers.length} lines`
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
